import { takeLatest, call, put, select, cancelled } from 'redux-saga/effects';
import i18next from 'i18next';

import * as trainingService from 'api/services/training';
import { parseParams } from 'helpers';
import {
  LOAD_MA_TRAININGS,
  CREATE_MA_TRAINING,
  UPDATE_MA_TRAINING,
  REMOVE_MA_TRAINING,
  IMPORT_MA_TRAININGS,
  LOAD_MA_CPD_DIPLOMAS,
  LOAD_MA_COMPETENCIES,
  LOAD_MA_CPD_COURSES,
} from '../store/constants';
import {
  setNewAlertCreator,
  setNewLoadingCreator,
  removeLoadingCreator,
  setMaTrainingsCreator,
  setMaTrainingsPagesCreator,
  setMaTrainingsTotalItemsCreator,
  setMaTrainingsCreateErrorsCreator,
  setMaTrainingsEditErrorsCreator,
  setMaTrainingsImportErrorsCreator,
  setMaCpdDiplomasCreator,
  setMaCompetenciesCreator,
  setMaCpdCoursesCreator,
} from '../store/actionsCreators';

const mapFieldErrors = (errors) =>
  Object.fromEntries(
    Object.entries(errors).map(([key, value]) => [key, Array.isArray(value) ? value[0] : value]),
  );

function* loadMaTrainings() {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const [filters, page, perPage, sort] = yield select(({ ma }) => [
      ma.trainings.filters,
      ma.trainings.page,
      ma.trainings.perPage,
      ma.trainings.sort,
    ]);

    const params = parseParams({
      maxResult: perPage,
      page,
      'sort-by': sort,
      ...filters,
    });

    const response = yield call(trainingService.getMaTrainingsList, params);
    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      yield put(setMaTrainingsCreator(items));
      yield put(setMaTrainingsPagesCreator(pageCount || 1));
      yield put(setMaTrainingsTotalItemsCreator(totalItems));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log('[GET] ma trainings error: ', error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* createMaTraining({ payload, history, redirectOnSuccess }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(trainingService.maCreateTraining, payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cpd',
          status: 'success',
          text: i18next.t('cpd.trainings.createSuccess'),
        }),
      );
    }
    if (redirectOnSuccess) history.push('/ma/cpd-management/trainings');
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(setMaTrainingsCreateErrorsCreator(mapFieldErrors(errors)));
    } else {
      yield put(setNewAlertCreator({ type: 'cpd', status: 'error', list: errors }));
    }
    console.log('[POST] create ma training error: ', error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* updateMaTraining({ id, payload, history, redirectOnSuccess = true }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(trainingService.maUpdateTraining, id, payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cpd',
          status: 'success',
          text: i18next.t('cpd.trainings.updateSuccess'),
        }),
      );
    }
    if (redirectOnSuccess) history.push('/ma/cpd-management/trainings');
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    const isSystemError = errors.length && !errors[0].path;

    if (!isSystemError) {
      yield put(setMaTrainingsEditErrorsCreator(mapFieldErrors(errors)));
    } else {
      yield put(setNewAlertCreator({ type: 'cpd', status: 'error', list: errors }));
    }
    console.log('[POST] update ma training error: ', error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* removeMaTraining({ payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(trainingService.maRemoveTraining, payload);
    if (response.ok) {
      yield put(
        setNewAlertCreator({
          type: 'cpd',
          status: 'success',
          text: i18next.t('cpd.trainings.removeSuccess'),
        }),
      );
      window.location.reload();
    }
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    yield put(setNewAlertCreator({ type: 'cpd', status: 'error', list: errors }));
    console.log('[DELETE] remove ma training error: ', error);
  }
}

function* importMaTrainings({ payload }) {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));
    const response = yield call(trainingService.importTrainingsData, payload);
    if (response.ok) {
      const {
        results: { result },
      } = yield response.json();
      if (result.errors && result.errors.length) {
        yield put(setMaTrainingsImportErrorsCreator(result.errors));
        yield put(setNewAlertCreator({ type: 'cpd', status: 'error', text: i18next.t('cpd.importFailed') }));
      } else {
        yield put(setMaTrainingsImportErrorsCreator([]));
        yield put(
          setNewAlertCreator({ type: 'cpd', status: 'success', text: i18next.t('global.importSuccess') }),
        );
      }
    }
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    const { errors } = yield error.response.json();
    yield put(setNewAlertCreator({ type: 'cpd', status: 'error', list: errors }));
    console.log('[POST] import ma trainings error: ', error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* loadMaCpdDiplomas() {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const [filters, page, perPage, sort] = yield select(({ ma }) => [
      ma.diplomas.filters,
      ma.diplomas.page,
      ma.diplomas.perPage,
      ma.diplomas.sort,
    ]);

    const params = parseParams({
      maxResult: perPage,
      page,
      'sort-by': sort,
      ...filters,
    });

    const response = yield call(trainingService.getMaCpdDiplomasList, params);
    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      yield put(setMaCpdDiplomasCreator({ items, pages: pageCount, totalItems }));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log('[GET] ma cpd diplomas error: ', error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* loadMaCompetencies() {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const [filters, page, perPage, sort] = yield select(({ ma }) => [
      ma.competencies.filters,
      ma.competencies.page,
      ma.competencies.perPage,
      ma.competencies.sort,
    ]);

    const params = parseParams({
      maxResult: perPage,
      page,
      'sort-by': sort,
      ...filters,
    });

    const response = yield call(trainingService.getMaCompetenciesList, params);
    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      yield put(setMaCompetenciesCreator({ items, pages: pageCount, totalItems }));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log('[GET] ma competencies error: ', error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

function* loadMaCpdCourses() {
  try {
    yield put(setNewLoadingCreator({ type: 'GLOBAL' }));

    const [filters, page, perPage, sort] = yield select(({ ma }) => [
      ma.cpdCourses.filters,
      ma.cpdCourses.page,
      ma.cpdCourses.perPage,
      ma.cpdCourses.sort,
    ]);

    const params = parseParams({
      maxResult: perPage,
      page,
      'sort-by': sort,
      ...filters,
    });

    const response = yield call(trainingService.getMaCpdCoursesList, params);
    if (response.ok) {
      const {
        results: {
          result: { items, pageCount, totalItems },
        },
      } = yield response.json();

      yield put(setMaCpdCoursesCreator({ items, pages: pageCount, totalItems }));
    }

    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  } catch (error) {
    yield put(removeLoadingCreator({ type: 'GLOBAL' }));
    console.log('[GET] ma cpd courses error: ', error);
  } finally {
    if (yield cancelled()) yield put(removeLoadingCreator({ type: 'GLOBAL' }));
  }
}

export default function* watchTraining() {
  yield takeLatest(LOAD_MA_TRAININGS, loadMaTrainings);
  yield takeLatest(CREATE_MA_TRAINING, createMaTraining);
  yield takeLatest(UPDATE_MA_TRAINING, updateMaTraining);
  yield takeLatest(REMOVE_MA_TRAINING, removeMaTraining);
  yield takeLatest(IMPORT_MA_TRAININGS, importMaTrainings);
  yield takeLatest(LOAD_MA_CPD_DIPLOMAS, loadMaCpdDiplomas);
  yield takeLatest(LOAD_MA_COMPETENCIES, loadMaCompetencies);
  yield takeLatest(LOAD_MA_CPD_COURSES, loadMaCpdCourses);
}
